import { useRef } from 'react';
import { ensureHttpsPrefix } from '@pkgs/api';
import { useModalState } from '@ev-tech/ui-core-components/Modal';

import { getOfficeNameForLabels } from '@/utils/propertyDataUtils';
import { ImprintModal } from '@/components/property-expose/ContactForm/components/ImprintModal';

import { useListing } from './queries/useListing';

import styles from '@/components/property-expose/ContactForm/ContactForm.module.css';

export const useConsentLabels = () => {
  const {
    data: { genericListing },
  } = useListing();
  const { privacyPolicyUrl, profile, officeName, shopName, agent, isPrivateOffice, imprintUrl: listingImprintUrl } = genericListing;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const imprintModalState = useModalState();

  const privacyUrl = privacyPolicyUrl ?? profile.shopDataSecurityUrl;

  const privacyUrlPrefixed = privacyUrl ? ensureHttpsPrefix(privacyUrl) : undefined;

  const entityName = isPrivateOffice ? agent?.legalEntityName : getOfficeNameForLabels(officeName, shopName);
  const dataSecurityURL = isPrivateOffice ? agent?.privacyPolicyUrl : privacyUrlPrefixed;
  const imprintUrl = isPrivateOffice ? agent?.imprintUrl : listingImprintUrl;

  const PrivacyLinkComponent = ({ children }: { children?: React.ReactNode }) => {
    if (!dataSecurityURL) return <span>{children}</span>;
    return (
      <a className={styles.link} data-testid="privacy-statement-link" href={dataSecurityURL} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  };

  const ImprintButtonComponent = ({ children }: { children?: React.ReactNode }) => {
    if (imprintUrl) {
      return (
        <a className={styles.link} data-testid="imprint-link" href={imprintUrl} target="_blank" rel="noreferrer">
          {children}
        </a>
      );
    }
    if (profile.imprint) {
      return (
        <button className={styles.imprintButton} type="button" ref={buttonRef} onClick={imprintModalState.open}>
          {children}
        </button>
      );
    }
    return <span>{entityName}</span>;
  };

  const ImprintModalComponent = () => {
    if (!profile.imprint) return null;
    return <ImprintModal imprint={profile.imprint} modalState={imprintModalState} />;
  };

  return {
    entityName,
    PrivacyLinkComponent,
    ImprintButtonComponent,
    ImprintModalComponent,
  };
};
